import { useContext, useEffect, useState } from "react";
import ReactGA from "react-ga4";
import Select from "react-select";
import RequestQuoteFormContext from "./RequestQuoteFormContext";
const Details = () => {
	const { details, setDetails, next } = useContext(RequestQuoteFormContext);
	const [fromOptions, setFromOptions] = useState([]);
	const [toOptions, setToOptions] = useState([]);

	const fetchPredictions = async (input, field) => {
		if (!input) return;
		if (window.google && window.google.maps) {
			try {
				const { AutocompleteSuggestion } =
					await window.google.maps.importLibrary("places");
				const { suggestions } =
					await AutocompleteSuggestion.fetchAutocompleteSuggestions({
						input: input,
					});
				const mapped = (suggestions || []).map((s) => ({
					value: s.placePrediction.placeId, // Better to use the actual Place ID
					label: s.placePrediction.text.toString(),
				}));
				const userInputOption = {
					value: 0,
					label: input,
				};
				if (field === "from") {
					setFromOptions([userInputOption, ...mapped]);
				} else if (field === "to") {
					setToOptions([userInputOption, ...mapped]);
				}
			} catch (err) {
				console.error("Error fetching predictions:", err);
			}
		}
	};

	useEffect(() => {
		fetchPredictions("", "from");
		fetchPredictions("", "to");
	}, []);

	const handleChange = (first, second) => {
		if (second && second.name) {
			setDetails((prev) => ({
				...prev,
				[second.name]: first,
			}));
			setFromOptions([]);
			setToOptions([]);
		} else if (first?.target) {
			const { name, value } = first.target;
			setDetails((prev) => ({ ...prev, [name]: value }));
		}
	};

	const handleInputChange = (fieldName) => (inputValue) => {
		if (inputValue) {
			fetchPredictions(inputValue, fieldName);
		}
	};

	const [errors, setErrors] = useState({});

	const handleSubmit = (e) => {
		e.preventDefault();
		const newErrors = {};

		if (
			!details.from ||
			typeof details.from !== "object" ||
			!details.from.label
		)
			newErrors.from = "From is required.";
		if (!details.to || typeof details.to !== "object" || !details.to.label)
			newErrors.to = "To is required.";
		if (
			!details.quantity ||
			isNaN(details.quantity) ||
			parseInt(details.quantity) <= 0
		)
			newErrors.quantity = "Quantity must be a positive integer.";
		if (
			!details.weight ||
			isNaN(details.weight) ||
			parseFloat(details.weight) <= 0
		)
			newErrors.weight = "Weight must be a positive number.";
		if (
			!details.length1 ||
			isNaN(details.length1) ||
			parseFloat(details.length1) <= 0
		)
			newErrors.length1 = "Length must be a positive number.";
		if (
			!details.width ||
			isNaN(details.width) ||
			parseFloat(details.width) <= 0
		)
			newErrors.width = "Width must be a positive number.";
		if (
			!details.height ||
			isNaN(details.height) ||
			parseFloat(details.height) <= 0
		)
			newErrors.height = "Height must be a positive number.";
		if (!details.description || details.description.trim() === "")
			newErrors.description = "Description is required.";

		setErrors(newErrors);

		if (Object.keys(newErrors).length > 0) {
			return;
		}

		ReactGA.event({
			category: "Quote Form",
			action: "quote_details_added",
		});

		next();
	};

	const selectTheme = (theme) => ({
		...theme,
		borderRadius: 2,
		colors: {
			...theme.colors,
			primary25: "var(--color-brand-100, #e0f5fe)",
			primary: "var(--color-brand-500, #2abaf7)",
		},
	});

	const selectStyles = {
		control: (base, state) => ({
			...base,
			borderColor: state.isFocused
				? "var(--color-brand-500, #2abaf7)"
				: "#e2e8f0",
			borderRadius: 2,
			boxShadow: "none",
			fontSize: "0.875rem",
			minHeight: "38px",
			"&:hover": { borderColor: "var(--color-brand-500, #2abaf7)" },
		}),
		placeholder: (base) => ({
			...base,
			color: "#94a3b8",
			fontSize: "0.875rem",
		}),
		option: (base) => ({ ...base, fontSize: "0.875rem" }),
	};

	return (
		<form className="w-full" onSubmit={(e) => e.preventDefault()}>
			{/* From / To */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
				<div className="flex flex-col gap-1">
					<label className="text-xs uppercase tracking-widest text-slate-400 font-medium">
						From
					</label>
					<Select
						className="basic-single"
						classNamePrefix="select"
						placeholder="City or address"
						isSearchable
						name="from"
						value={details.from}
						onChange={handleChange}
						onInputChange={handleInputChange("from")}
						options={fromOptions}
						theme={selectTheme}
						styles={selectStyles}
					/>
					{errors?.from && (
						<span className="text-red-400 text-xs">{errors.from}</span>
					)}
				</div>
				<div className="flex flex-col gap-1">
					<label className="text-xs uppercase tracking-widest text-slate-400 font-medium">
						To
					</label>
					<Select
						className="basic-single"
						classNamePrefix="select"
						placeholder="City or address"
						isSearchable
						name="to"
						value={details.to}
						onChange={handleChange}
						onInputChange={handleInputChange("to")}
						options={toOptions}
						theme={selectTheme}
						styles={selectStyles}
					/>
					{errors?.to && (
						<span className="text-red-400 text-xs">{errors.to}</span>
					)}
				</div>
			</div>

			{/* Quantity & Weight */}
			<div className="grid grid-cols-2 gap-3 mb-4">
				<div className="flex flex-col gap-1">
					<label className="text-xs uppercase tracking-widest text-slate-400 font-medium">
						Quantity
					</label>
					<input
						type="text"
						name="quantity"
						value={details.quantity}
						onChange={handleChange}
						placeholder="0"
						className="border border-slate-200 rounded-sm px-3 py-2 text-sm text-slate-900 placeholder:text-slate-300 focus:outline-none focus:border-brand-500 transition-colors"
					/>
					{errors?.quantity && (
						<span className="text-red-400 text-xs">{errors.quantity}</span>
					)}
				</div>
				<div className="flex flex-col gap-1">
					<label className="text-xs uppercase tracking-widest text-slate-400 font-medium">
						Weight{" "}
						<span className="normal-case tracking-normal text-slate-300">
							kg
						</span>
					</label>
					<input
						type="text"
						name="weight"
						value={details.weight}
						onChange={handleChange}
						placeholder="0.0"
						className="border border-slate-200 rounded-sm px-3 py-2 text-sm text-slate-900 placeholder:text-slate-300 focus:outline-none focus:border-brand-500 transition-colors"
					/>
					{errors?.weight && (
						<span className="text-red-400 text-xs">{errors.weight}</span>
					)}
				</div>
			</div>

			{/* Dimensions */}
			<div className="flex flex-col gap-1 mb-4">
				<label className="text-xs uppercase tracking-widest text-slate-400 font-medium">
					Dimensions{" "}
					<span className="normal-case tracking-normal text-slate-300">cm</span>
				</label>
				<div className="grid grid-cols-3 gap-3">
					<div className="flex flex-col gap-1">
						<input
							type="text"
							name="height"
							value={details.height}
							onChange={handleChange}
							placeholder="Height"
							className="border border-slate-200 rounded-sm px-3 py-2 text-sm text-slate-900 placeholder:text-slate-300 focus:outline-none focus:border-brand-500 transition-colors"
						/>
						{errors?.height && (
							<span className="text-red-400 text-xs">{errors.height}</span>
						)}
					</div>
					<div className="flex flex-col gap-1">
						<input
							type="text"
							name="width"
							value={details.width}
							onChange={handleChange}
							placeholder="Width"
							className="border border-slate-200 rounded-sm px-3 py-2 text-sm text-slate-900 placeholder:text-slate-300 focus:outline-none focus:border-brand-500 transition-colors"
						/>
						{errors?.width && (
							<span className="text-red-400 text-xs">{errors.width}</span>
						)}
					</div>
					<div className="flex flex-col gap-1">
						<input
							type="text"
							name="length1"
							value={details.length1}
							onChange={handleChange}
							placeholder="Length"
							className="border border-slate-200 rounded-sm px-3 py-2 text-sm text-slate-900 placeholder:text-slate-300 focus:outline-none focus:border-brand-500 transition-colors"
						/>
						{errors?.length1 && (
							<span className="text-red-400 text-xs">{errors.length1}</span>
						)}
					</div>
				</div>
			</div>

			{/* Description */}
			<div className="flex flex-col gap-1 mb-6">
				<label className="text-xs uppercase tracking-widest text-slate-400 font-medium">
					What are you sending?
				</label>
				<textarea
					rows={3}
					name="description"
					value={details.description}
					onChange={handleChange}
					placeholder="e.g. Electronics, clothing, documents…"
					className="border border-slate-200 rounded-sm px-3 py-2 text-sm text-slate-900 placeholder:text-slate-300 focus:outline-none focus:border-brand-500 transition-colors resize-none"
				/>
				{errors?.description && (
					<span className="text-red-400 text-xs">{errors.description}</span>
				)}
			</div>

			{/* Submit */}
			<button
				onClick={handleSubmit}
				className="w-full py-2.5 bg-brand-500 ha:bg-brand-600 active:scale-[0.99] text-white text-sm font-medium tracking-wide transition-all rounded-sm"
			>
				Continue →
			</button>
		</form>
	);
};
export default Details;
